import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CDN_URL, PARKI_WEBSITE } from '../common/constants';
import { shouldLogUsage } from '../../utils/helpers';
import { logReportView } from '../../utils/api_dashboard';
import IntegrationsStatusWidget from './IntegrationsStatusWidget';

const Sidebar = ({ 
    activeItem = 'dashboard', 
    collapsed, 
    toggleSidebar, 
    reports = [],
    selectedReport,
    onSelectReport,
    anomalyAlerts,
    customLogo,
    user = {}
}) => {
    const [menuState, setMenuState] = useState({
        bookings: false,
        financials: false,
        benchmarking: false,
        other: false
    });

    // Add a hovering state for hover behavior
    const [hovering, setHovering] = useState(false);
    const menuInnerRef = useRef(null);
    const psRef = useRef(null);

    // Initialize Perfect Scrollbar once
    useEffect(() => {
        const el = menuInnerRef.current;
        if (!el) return;

        // If Vuexy's Menu already initialized PS, skip double init
        const alreadyPS = el.classList.contains('ps');
        if (!alreadyPS && window.PerfectScrollbar) {
        psRef.current = new window.PerfectScrollbar(el, {
            wheelPropagation: false,
            suppressScrollX: true,
            // The menu's height comes from flexbox and can be fractional; without this 2px allowance
            // rounding makes the content look 1px taller and a needless scrollbar appears
            scrollYMarginOffset: 2
        });
        }

        return () => {
        if (psRef.current && psRef.current.destroy) {
            psRef.current.destroy();
            psRef.current = null;
        }
        };
    }, []);

    // Update PS when content/height can change
    useEffect(() => {
        if (psRef.current && psRef.current.update) {
        psRef.current.update();
        } else if (window.Menu && window.Menu.init) {
        // Fallback: ask Vuexy to recalc if it manages PS
        try { window.Menu.init(); } catch {}
        }
    }, [reports.length, collapsed, hovering, menuState]);

    // Update PS when the menu's height changes, for example when the integrations widget loads below it
    useEffect(() => {
        const el = menuInnerRef.current;
        if (!el || !window.ResizeObserver) return;

        // Recalculate the scrollbar each time the menu is resized
        const observer = new window.ResizeObserver(() => {
            if (psRef.current && psRef.current.update) {
                psRef.current.update();
            }
        });
        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    // Show the integrations widget only to Entity Admins, compact when the sidebar is collapsed
    const isEntityAdmin = user?.isEntityAdmin === true || user?.isEntityAdmin === 1 || user?.isEntityAdmin === '1';
    const widgetCompact = collapsed && !hovering;

    // Extract tile reports
    const tileReports = reports.filter(report => report.locationType === 'tile');
    
    // Find Anomaly Detection tile if it exists
    const anomalyDetection = tileReports.find(
        report => report.reportName === 'Anomaly Detection' && report.uriPath
    );
    
    // Other tiles (excluding Anomaly Detection)
    const otherTiles = tileReports.filter(
        report => report.reportName !== 'Anomaly Detection'
    );
    
    // Group reports by category
    const reportCategories = {
        bookings: reports.filter(report => report.dashboardHeading === 'bookings' && report.locationType === 'dashboard'),
        financials: reports.filter(report => report.dashboardHeading === 'financials' && report.locationType === 'dashboard'),
        benchmarking: reports.filter(report => report.dashboardHeading === 'benchmarking' && report.locationType === 'dashboard'),
        other: reports.filter(report => report.dashboardHeading === 'other' && report.locationType === 'dashboard')
    };

    // This effect adds collapsed class to body when sidebar state changes
    useEffect(() => {
        const addClassToDocument = (className) => {
            document.body.classList.add(className);
            document.documentElement.classList.add(className);
        };

        const removeClassFromDocument = (className) => {
            document.body.classList.remove(className);
            document.documentElement.classList.remove(className);
        };

        if (collapsed) {
            addClassToDocument('layout-menu-collapsed');
            removeClassFromDocument('layout-menu-expanded');
            localStorage.setItem('sidebarCollapsed', 'true');
        } else {
            removeClassFromDocument('layout-menu-collapsed');
            addClassToDocument('layout-menu-expanded'); 
            localStorage.setItem('sidebarCollapsed', 'false');
        }
        
        if (hovering) {
            addClassToDocument('layout-menu-hover');
        } else {
            removeClassFromDocument('layout-menu-hover');
        }

        return () => {
            removeClassFromDocument('layout-menu-collapsed');
            removeClassFromDocument('layout-menu-hover');
        };
    }, [collapsed, hovering]);

    // Handle report selection
    const handleReportClick = (report) => (event) => {
        event.preventDefault();
        if (onSelectReport) {
            onSelectReport(report);
        }
    };

    // Update the sidebar toggle to use the passed function
    const handleToggleSidebar = (event) => {
        event.preventDefault();
        toggleSidebar();
    };

    // Mouse enter/leave handlers
    const handleMouseEnter = () => {
        if (collapsed) {
            setHovering(true);
        }
    };

    const handleMouseLeave = () => {
        setHovering(false);
    };

    // Toggle menu category
    const toggleMenu = (menu) => (event) => {
        event.preventDefault();
        setMenuState(prevState => ({
            ...prevState,
            [menu]: !prevState[menu]
        }));
    };

    // Check if any report in a category is selected
    const isReportCategoryActive = (category) => {
        if (!selectedReport) return false;
        return reportCategories[category].some(report => report.reportId === selectedReport.reportId);
    };

    // declare an 'Other Report' click eg - TV Screen / Email Alert
    const onOtherReportClick = (reportId) => (event) => {
        event.preventDefault();

        // get idToken from local storage if it exists
        const idToken = localStorage.getItem('idToken');

        // get the uriPath
        var uriPath = '';
        var reportName = '';
        switch (String(reportId)) {
            case '40':  // TV Screen
                uriPath = 'my-account/tv-screens/';
                reportName = 'TV Screen';
                break;
            case '41': // Email Alert
                uriPath = 'my-account/email-notifications/';
                reportName = 'Email Alert';
                break;
            default:
                uriPath = ''
        }

        if(shouldLogUsage()) {
            // Log the report view
            logReportView({
                reportId: reportId,
                reportName: reportName
            });
        }

        // set the url and open a new tab
        let url = PARKI_WEBSITE + '/sso-login?token=' + idToken + '&redirect_to=' + encodeURIComponent(uriPath);
        window.open(url, '_blank');



    };

    return (
        <>
            {/* Menu Sidebar */}
            <aside 
                id="layout-menu" 
                className={`layout-menu menu-vertical menu ${isEntityAdmin ? 'has-integrations-widget' : ''}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <div className="app-brand demo">
                    <Link to="/" className="app-brand-link">
                        <span className="app-brand-logo demo">
                            {/* Full logo - shown when menu expanded */}
                            <img
                                src={customLogo?.hasCustomLogo && customLogo.logo_path 
                                    ? customLogo.logo_path 
                                    : `${CDN_URL}/parki/logo/Logo+files/PNGs+-+SVGs/SVG/Asset+5.svg`}
                                alt="Logo"
                                className="w-px-130 h-auto expanded-logo"
                                style={{ display: (!collapsed || (collapsed && hovering)) ? 'block' : 'none' }}
                            />
                            {/* Emblem logo - shown when menu collapsed */}
                            <img
                                src={customLogo?.hasCustomLogo && customLogo.logo_thumb_path 
                                    ? customLogo.logo_thumb_path 
                                    : `${CDN_URL}/parki/logo/Logo+files/Emblem/SVG/Asset+3.svg`}
                                alt="Logo"
                                className="w-px-40 h-auto collapsed-logo"
                                style={{ display: (collapsed && !hovering) ? 'block' : 'none' }}
                            />
                        </span>
                        <span className="app-brand-text demo menu-text fw-bold ms-3"></span>
                    </Link>
                    <a href="#" onClick={handleToggleSidebar} className="layout-menu-toggle menu-link text-large ms-auto">
                        <i className="icon-base ti menu-toggle-icon d-none d-xl-block"></i>
                        <i className="icon-base ti tabler-x d-block d-xl-none"></i>
                    </a>
                </div>

                <div className="menu-inner-shadow"></div>

                <ul 
                    className="menu-inner py-1"
                    ref={menuInnerRef}
                >
                    {/* Anomaly Detection - Above Dashboard Header */}
                    {anomalyDetection && (
                        <li className={`menu-item ${selectedReport?.reportId === anomalyDetection.reportId ? 'active' : ''}`}>
                            <a href="#" onClick={handleReportClick(anomalyDetection)} className="menu-link">
                                {anomalyDetection.iconPath && (
                                    <img src={anomalyDetection.iconPath} alt="" className="w-px-20 h-auto menu-icon" />
                                )}
                                {!anomalyDetection.iconPath && (
                                    <i className="menu-icon icon-base ti tabler-alert-triangle"></i>
                                )}
                                <div data-i18n={anomalyDetection.reportFriendly}>{anomalyDetection.reportFriendly}</div>
                                {/* {if we have a 'high' anomaly alert */}
                                {anomalyAlerts?.high && anomalyAlerts.high > 0 && (
                                    <div className="text-xs badge mx-1 text-bg-danger rounded-pill ms-auto">{anomalyAlerts.high}</div>
                                )}
                                {/* {if we have a 'medium' anomaly alert */}
                                {anomalyAlerts?.medium && anomalyAlerts.medium > 0 && (
                                    <div className="text-xs badge mx-1 text-bg-warning rounded-pill ms-auto">{anomalyAlerts.medium}</div>
                                )}
                                {/* {if we have a 'low' anomaly alert */}
                                {anomalyAlerts?.low && anomalyAlerts.low > 0 && (
                                    <div className="text-xs badge mx-1 text-bg-info rounded-pill ms-auto">{anomalyAlerts.low}</div>
                                )}
                            </a>
                        </li>
                    )}

                    {/* Dashboards Header */}
                    <li className="menu-header small">
                        <span className="menu-header-text">Dashboards</span>
                    </li>

                    {/* Bookings Reports */}
                    {reportCategories.bookings.length > 0 && (
                        <li className={`menu-item ${menuState.bookings || isReportCategoryActive('bookings') ? 'open' : ''}`}>
                            <a 
                                href="#" 
                                onClick={toggleMenu('bookings')} 
                                className="menu-link menu-toggle"
                            >
                                <span className="avatar avatar-xs me-2">
                                    <img src={CDN_URL + '/parki/report_icons/calendar-circle-user-regular-full.svg'} alt="" className="w-px-20 h-auto" />
                                </span>
                                <div data-i18n="Bookings">Bookings</div>
                            </a>
                            <ul className="menu-sub">
                                {reportCategories.bookings.map(report => (
                                    <li 
                                        key={report.reportId} 
                                        className={`menu-item ${selectedReport?.reportId === report.reportId ? 'active' : ''}`}
                                    >
                                        <a href="#" onClick={handleReportClick(report)} className="menu-link">
                                            {report.iconPath && (
                                                <span className="avatar avatar-xs me-2">
                                                    <img src={report.iconPath} alt="" className="w-px-20 h-auto" />
                                                </span>
                                            )}
                                            <div data-i18n={report.reportFriendly}>{report.reportFriendly}</div>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )}

                    {/* Financials Reports */}
                    {reportCategories.financials.length > 0 && (
                        <li className={`menu-item ${menuState.financials || isReportCategoryActive('financials') ? 'open' : ''}`}>
                            <a 
                                href="#" 
                                onClick={toggleMenu('financials')} 
                                className="menu-link menu-toggle"
                            >
                                <span className="avatar avatar-xs me-2">
                                    <img src={CDN_URL + '/parki/report_icons/box-dollar-regular-full.svg'} alt="" className="w-px-20 h-auto" />
                                </span>
                                <div data-i18n="Financials">Financials</div>
                            </a>
                            <ul className="menu-sub">
                                {reportCategories.financials.map(report => (
                                    <li 
                                        key={report.reportId} 
                                        className={`menu-item ${selectedReport?.reportId === report.reportId ? 'active' : ''}`}
                                    >
                                        <a href="#" onClick={handleReportClick(report)} className="menu-link">
                                            {report.iconPath && (
                                                <span className="avatar avatar-xs me-2">
                                                    <img src={report.iconPath} alt="" className="w-px-20 h-auto" />
                                                </span>
                                            )}
                                            <div data-i18n={report.reportFriendly}>{report.reportFriendly}</div>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )}

                    {/* Benchmarking Reports */}
                    {reportCategories.benchmarking.length > 0 && (
                        <li className={`menu-item ${menuState.benchmarking || isReportCategoryActive('benchmarking') ? 'open' : ''}`}>
                            <a 
                                href="#" 
                                onClick={toggleMenu('benchmarking')} 
                                className="menu-link menu-toggle"
                            >
                                <span className="avatar avatar-xs me-2">
                                    <img src={CDN_URL + '/parki/report_icons/chart-simple-solid-full.svg'} alt="" className="w-px-20 h-auto" />
                                </span>
                                <div data-i18n="Benchmarking">Benchmarking</div>
                            </a>
                            <ul className="menu-sub">
                                {reportCategories.benchmarking.map(report => (
                                    <li 
                                        key={report.reportId} 
                                        className={`menu-item ${selectedReport?.reportId === report.reportId ? 'active' : ''}`}
                                    >
                                        <a href="#" onClick={handleReportClick(report)} className="menu-link">
                                            {report.iconPath && (
                                                <span className="avatar avatar-xs me-2">
                                                    <img src={report.iconPath} alt="" className="w-px-20 h-auto" />
                                                </span>
                                            )}
                                            <div data-i18n={report.reportFriendly}>{report.reportFriendly}</div>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )}

                    {/* Other Reports */}
                    {reportCategories.other.length > 0 && (
                        <li className={`menu-item ${menuState.other || isReportCategoryActive('other') ? 'open' : ''}`}>
                            <a 
                                href="#" 
                                onClick={toggleMenu('other')} 
                                className="menu-link menu-toggle"
                            >
                                <span className="avatar avatar-xs me-2">
                                    <img src={CDN_URL + '/parki/report_icons/grid-2-plus-regular-full.svg'} alt="" className="w-px-20 h-auto" />
                                </span>
                                <div data-i18n="Other">Other</div>
                            </a>
                            <ul className="menu-sub">
                                {reportCategories.other.map(report => (
                                    <li 
                                        key={report.reportId} 
                                        className={`menu-item ${selectedReport?.reportId === report.reportId ? 'active' : ''}`}
                                    >
                                        <a href="#" onClick={handleReportClick(report)} className="menu-link">
                                            {report.iconPath && (
                                                <span className="avatar avatar-xs me-2">
                                                    <img src={report.iconPath} alt="" className="w-px-20 h-auto" />
                                                </span>
                                            )}
                                            <div data-i18n={report.reportFriendly}>{report.reportFriendly}</div>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )}

                    {/* Dashboards Header */}
                    <li className="menu-header small">
                        <span className="menu-header-text">TV & Email</span>
                    </li>

                    {/* Other Tiles (non-Anomaly Detection) - Below Dashboard Header */}
                    {otherTiles.map(tile => (
                        <li 
                            key={tile.reportId} 
                            className={`menu-item ${selectedReport?.reportId === tile.reportId ? 'active' : ''}`}
                        >
                            <a href="#" onClick={onOtherReportClick(tile.reportId)} className="menu-link">
                                {tile.iconPath && (
                                    <span className="avatar avatar-xs me-2">
                                        <img src={tile.iconPath} alt="" className="w-px-20 h-auto" />
                                    </span>
                                )}
                                <div data-i18n={tile.reportFriendly}>{tile.reportFriendly}</div>
                            </a>
                        </li>
                    ))}

                    {/* Your existing category groups... */}
                    {/* Bookings Reports */}
                    {reportCategories.bookings.length > 0 && (
                        <li className={`menu-item ${menuState.bookings || isReportCategoryActive('bookings') ? 'open' : ''}`}>
                            {/* ...existing code... */}
                        </li>
                    )}


                </ul>

                {/* Integrations status, pinned below the scrolling menu */}
                {isEntityAdmin && (
                    <IntegrationsStatusWidget user={user} compact={widgetCompact} />
                )}
            </aside>

            {/* Mobile Menu Toggle */}
            <div className="menu-mobile-toggler d-xl-none rounded-1">
                <a 
                    href="#" 
                    onClick={toggleSidebar} 
                    className="layout-menu-toggle menu-link text-large text-bg-secondary p-2 rounded-1"
                >
                    <i className="ti tabler-menu icon-base"></i>
                    <i className="ti tabler-chevron-right icon-base"></i>
                </a>
            </div>
        </>
    );
};

export default Sidebar;